import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
import {type Query} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientInterface/query';
import {type TraceCallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';
import {
  traceCallStatusCode,
  useCalls,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {useMemo, useState} from 'react';

import {STATUS_VALUE_MAPPING, WEAVE_STATUS_PATH} from '../constants';
import {
  type AggregationFunction,
  type WeaveMetricFilter,
  type WeaveMetricThresholdConfig,
} from '../types';
import {buildMonitorInputRefs, getColumnRoot, getValue} from '../utils';
import {isValidThreshold} from '../validation';

export type TimeRange = '1h' | '1d' | '1w';

export type ChartDataPoint = {
  timestamp: number;
  value: number | null;
  aggregatedValue: number | null;
};

const DURATIONS: Record<TimeRange, number> = {
  '1h': 3_600_000,
  '1d': 86_400_000,
  '1w': 604_800_000,
};

/**
 * Format a timestamp for display on the chart axis, adapting precision to the
 * selected time range (time-only for 1h, date+time for 1d, date-only for 1w).
 */
export const formatTime = (ts: number, range: TimeRange): string => {
  const date = new Date(ts);
  if (range === '1h') {
    return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
  }
  if (range === '1d') {
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString([], {month: 'short', day: 'numeric'});
};

/** Reduce an array of numeric values to a single value using the given aggregation function. */
const aggregate = (
  values: number[],
  fn: AggregationFunction
): number | null => {
  if (values.length === 0) {
    return null;
  }
  switch (fn) {
    case 'mean':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    }
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    case 'mode': {
      const counts = new Map<number, number>();
      for (const v of values) {
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      let best = values[0];
      let bestCount = 0;
      for (const [v, c] of counts) {
        if (c > bestCount) {
          best = v;
          bestCount = c;
        }
      }
      return best;
    }
    case 'percentage':
    case 'count':
      // These are handled separately for status metrics
      return null;
    default:
      return null;
  }
};

/**
 * For status metrics with percentage/count aggregation, compute the aggregated
 * value over a window of raw status values.
 */
const aggregateStatus = (
  rawValues: (number | null)[],
  fn: AggregationFunction,
  statusMatchValue: number
): number | null => {
  const values = rawValues.filter((v): v is number => v !== null);
  if (values.length === 0) {
    return null;
  }
  const matchCount = values.filter(v => v === statusMatchValue).length;
  if (fn === 'percentage') {
    return (matchCount / values.length) * 100;
  }
  if (fn === 'count') {
    return matchCount;
  }
  return null;
};

type RawPoint = {
  timestamp: number;
  value: number | null;
};

const parseStatusMatchValue = (value: string | undefined): number => {
  if (value == null || value === '') {
    return 0;
  }

  if (value in STATUS_VALUE_MAPPING) {
    return STATUS_VALUE_MAPPING[value as keyof typeof STATUS_VALUE_MAPPING];
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Epoch-ms timestamp for a call's start. The trace server returns started_at as
 * a UTC string without the `Z` suffix, so parse it as UTC rather than letting
 * `new Date` assume local time (which shifts the chart by the local offset).
 */
export const getChartTimestamp = (startedAt: string | undefined): number =>
  startedAt ? unixTimestampMSFromUTCString(startedAt) : 0;

export const getChartMetricValue = (
  traceCall: TraceCallSchema,
  metricPath: string
): number | null => {
  if (metricPath === WEAVE_STATUS_PATH) {
    const status = traceCallStatusCode(traceCall);
    return STATUS_VALUE_MAPPING[status];
  }

  const raw = getValue(traceCall, metricPath);
  if (typeof raw === 'number') {
    return raw;
  }
  if (typeof raw === 'boolean') {
    return raw ? 1 : 0;
  }
  return null;
};

/**
 * Apply a sliding-window aggregation over raw data points to produce chart-ready
 * data. Each output point includes both the raw value and the windowed aggregate.
 */
const computeSlidingWindow = (
  points: RawPoint[],
  metricFilter: Partial<WeaveMetricFilter>,
  range: TimeRange
): ChartDataPoint[] => {
  const aggFn = metricFilter.aggregationFunction ?? 'mean';
  const windowSize = metricFilter.windowSize;
  const windowDurationMs = metricFilter.windowDuration
    ? metricFilter.windowDuration * 1000
    : undefined;
  const isStatus = metricFilter.metricPath === WEAVE_STATUS_PATH;
  const statusMatchValue = parseStatusMatchValue(metricFilter.statusMatchValue);

  return points.map((point, i) => {
    let windowValues: (number | null)[];

    if (windowSize && windowDurationMs) {
      // Both: last N calls within the time window
      const timeFloor = point.timestamp - windowDurationMs;
      const inTimeWindow = points
        .slice(0, i + 1)
        .filter(p => p.timestamp >= timeFloor);
      windowValues = inTimeWindow.slice(-windowSize).map(p => p.value);
    } else if (windowDurationMs) {
      // Time-based window
      const timeFloor = point.timestamp - windowDurationMs;
      windowValues = points
        .slice(0, i + 1)
        .filter(p => p.timestamp >= timeFloor)
        .map(p => p.value);
    } else if (windowSize) {
      // Count-based window
      const start = Math.max(0, i + 1 - windowSize);
      windowValues = points.slice(start, i + 1).map(p => p.value);
    } else {
      // No window config — just use raw value
      windowValues = [point.value];
    }

    let aggregatedValue: number | null;
    if (isStatus && (aggFn === 'percentage' || aggFn === 'count')) {
      aggregatedValue = aggregateStatus(windowValues, aggFn, statusMatchValue);
    } else {
      const numericValues = windowValues.filter((v): v is number => v !== null);
      aggregatedValue = aggregate(numericValues, aggFn);
    }

    return {
      timestamp: point.timestamp,
      value: point.value,
      aggregatedValue,
    };
  });
};

export const getChartThresholdValue = (
  threshold: string | undefined,
  metricPath: string
): number | null => {
  const metricType = metricPath === WEAVE_STATUS_PATH ? 'status' : undefined;
  return isValidThreshold(threshold, metricType)
    ? parseFloat(threshold!)
    : null;
};

export const useWeaveMetricChartData = (
  entityName: string,
  projectName: string,
  config: WeaveMetricThresholdConfig
) => {
  const [range, setRange] = useState<TimeRange>('1h');

  const {metricFilter} = config;
  const opStr = metricFilter.opStr ?? '';
  const metricPath = metricFilter.metricPath ?? '';
  const threshold = getChartThresholdValue(metricFilter.threshold, metricPath);

  // oxlint-disable-next-line react/purity
  const rangeEnd = useMemo(() => Date.now(), [range]); // oxlint-disable-line react-hooks/exhaustive-deps
  const cutoffMs = rangeEnd - DURATIONS[range];
  const cutoffSec = cutoffMs / 1000;

  const query: Query = useMemo(
    () => ({
      $expr: {
        $gt: [{$getField: 'started_at'}, {$literal: cutoffSec}],
      },
    }),
    [cutoffSec]
  );

  // For monitor sources, opStr is a scorer object ref (not an op version ref).
  // Scorer calls have the scorer object ref in their input_refs, so we must
  // filter by inputObjectVersionRefs to find the correct calls.
  const isMonitor =
    metricFilter.sourceType === 'MONITOR' || !!metricFilter.monitorRef;
  const monitorRef = metricFilter.monitorRef;
  const inputObjectVersionRefs = useMemo(
    () =>
      isMonitor
        ? buildMonitorInputRefs(opStr ? [opStr] : [], monitorRef)
        : undefined,
    [isMonitor, opStr, monitorRef]
  );
  const filter = isMonitor
    ? {inputObjectVersionRefs: inputObjectVersionRefs ?? []}
    : {opVersionRefs: opStr ? [opStr] : []};

  const includeCosts = metricPath.startsWith('summary.weave.costs.');

  const hasRequiredRefs = isMonitor ? inputObjectVersionRefs?.length : opStr;

  const {loading, result, error} = useCalls({
    entity: entityName,
    project: projectName,
    filter,
    query,
    columns: ['id', 'started_at', getColumnRoot(metricPath)],
    sortBy: [{field: 'started_at', direction: 'asc'}],
    limit: 500,
    skip: !hasRequiredRefs,
    includeCosts,
  });

  const hasWindow = !!(metricFilter.windowSize || metricFilter.windowDuration);
  const aggFn = metricFilter.aggregationFunction ?? 'mean';
  const isStatus = metricPath === WEAVE_STATUS_PATH;

  const chartData: ChartDataPoint[] = useMemo(() => {
    if (!result) {
      return [];
    }
    const rawPoints: RawPoint[] = result
      .map(call => {
        const ts = getChartTimestamp(call.traceCall?.started_at);
        const value = call.traceCall
          ? getChartMetricValue(call.traceCall, metricPath)
          : null;
        return {timestamp: ts, value};
      })
      .filter(d => d.timestamp > 0);

    return computeSlidingWindow(rawPoints, metricFilter, range);
  }, [result, metricPath, metricFilter, range]);

  const timeDomain: [number, number] = [cutoffMs, rangeEnd];

  return {
    chartData,
    loading,
    error,
    range,
    setRange,
    threshold,
    metricPath,
    hasWindow,
    aggFn,
    isStatus,
    timeDomain,
  };
};
